import type { DbExecutor } from "./executor";
import type { ProposedCostRepository } from "./repositories/proposedCostRepository";
import type { ExpenseRepository } from "./repositories/expenseRepository";
import type { PurchaseRepository } from "./repositories/purchaseRepository";
import type { MileageRepository } from "./repositories/mileageRepository";
import type { FeatureRepository } from "./repositories/featureRepository";
import type { LaborSettlementRepository } from "./repositories/laborSettlementRepository";
import type { ProcurementService } from "./procurementService";
import type { AttachmentRepository } from "./repositories/attachmentRepository";
import { projectActualEvents, type ActualCostEvent } from "../engine/actualEvents";
import {
  quantityComparison,
  reconcile,
  type BasisCompletenessInput,
  type CostBasis,
  type QuantityComparison,
  type ReconciliationResult,
} from "../engine/reconciliation";
import { computePurchase } from "../engine/purchaseMath";
import type { Attachment } from "../domain/attachment";
import type { Cents } from "../domain/money";

/**
 * Reconciliation read-model (Phase 9C).
 *
 * Assembles the inputs the pure engine needs from the EXISTING
 * authoritative records — proposed lines of the Active Design, the
 * normalized ActualCostEvent stream, procurement fulfillment, and labor
 * settlements — and resolves provenance for any actual number the user
 * clicks. It stores nothing: there is no "actual cost" column anywhere.
 */

export interface ProvenanceLine {
  label: string;
  amountCents: Cents;
  detail: string | null;
}

export interface ProvenanceRecord {
  eventId: string;
  source: "expense" | "allocation" | "mileage";
  /** "Home Depot purchase", "Manual entry", "Mileage" … */
  sourceLabel: string;
  vendor: string;
  date: string;
  description: string;
  receiptNumber: string | null;
  /** Merchandise/base amount attributed to this project. */
  amountCents: Cents;
  taxCents: Cents;
  totalCents: Cents;
  /** True when this record reduces cost (a return/refund). */
  isReturn: boolean;
  /** Breakdown of what made up the number. */
  lines: ProvenanceLine[];
  /** Receipts/invoices already attached (Phase 9D will parse these). */
  attachments: Attachment[];
  /** Deep-link targets so the UI can jump to the underlying record. */
  purchaseId: string | null;
  /** The purchase line, so Phase 9D can highlight its receipt region. */
  purchaseLineId: string | null;
  expenseId: string | null;
}

export class ReconciliationService {
  constructor(
    private readonly db: DbExecutor,
    private readonly proposedCosts: ProposedCostRepository,
    private readonly expenses: ExpenseRepository,
    private readonly purchases: PurchaseRepository,
    private readonly mileage: MileageRepository,
    private readonly features: FeatureRepository,
    private readonly settlements: LaborSettlementRepository,
    private readonly procurement: ProcurementService,
    private readonly attachments: AttachmentRepository,
  ) {}

  /** All non-void actual events for a project (the one actual truth). */
  async eventsForProject(projectId: string): Promise<ActualCostEvent[]> {
    const [expenses, graphs, mileage] = await Promise.all([
      this.expenses.listByProject(projectId),
      this.purchases.listTouchingProject(projectId),
      this.mileage.listByProject(projectId),
    ]);
    return projectActualEvents(projectId, expenses, graphs, mileage);
  }

  private async activeDesignId(projectId: string): Promise<string | null> {
    const rows = await this.db.select<{ active_design_id: string | null }>(
      "SELECT active_design_id FROM projects WHERE id = ?",
      [projectId],
    );
    return rows[0]?.active_design_id ?? null;
  }

  /**
   * Reconcile the project: the ACTIVE design's proposed costs (plus common
   * scope) against the project's actual accounting. Switching the active
   * design changes only the proposed side — actual records are never
   * touched, moved or duplicated.
   */
  async forProject(projectId: string, designId?: string | null): Promise<ReconciliationResult> {
    const evalDesign = designId === undefined ? await this.activeDesignId(projectId) : designId;
    const [proposedLines, events, features, settlementRows, lineIndex, procurementRows] = await Promise.all([
      this.proposedCosts.listForDesign(projectId, evalDesign),
      this.eventsForProject(projectId),
      this.features.listByProject(projectId),
      this.settlements.listByProject(projectId),
      this.settlements.lineIndexForProject(projectId),
      this.procurement.forProject(projectId).catch(() => []),
    ]);

    // Feature metadata (name + where it lives + category for grouping).
    const rooms = await this.db.select<{ id: string; name: string; floor_name: string }>(
      `SELECT r.id, r.name, fl.name AS floor_name FROM rooms r JOIN floors fl ON fl.id = r.floor_id
        WHERE fl.project_id = ?`,
      [projectId],
    );
    const roomLabel = new Map(rooms.map((r) => [r.id, `${r.floor_name} → ${r.name}`]));
    const lineCategory = new Map<string, string | null>();
    for (const line of proposedLines) {
      if (line.featureId && line.categoryId && !lineCategory.has(line.featureId)) {
        lineCategory.set(line.featureId, line.categoryId);
      }
    }
    const featureMeta = features
      .filter((f) => f.archivedAt === null)
      .map((f) => ({
        id: f.id,
        name: f.name,
        categoryId: lineCategory.get(f.id) ?? null,
        scopeLabel: f.roomId ? (roomLabel.get(f.roomId) ?? "Room") : "Project / Common",
      }));

    // Completeness + quantity evidence from procurement (same source the
    // buy list uses — never a second opinion about what was purchased).
    const completeness = new Map<string, Partial<Record<CostBasis, BasisCompletenessInput>>>();
    const quantities = new Map<string, QuantityComparison>();
    for (const row of procurementRows) {
      for (const featureId of row.featureIds) {
        const entry = completeness.get(featureId) ?? {};
        const material = entry.material ?? {};
        const status = row.fulfillment.status;
        if (status === "purchased") material.procurementComplete = true;
        else if (status === "partially-purchased") material.procurementPartial = true;
        else if (status === "crew-supplied" || status === "not-required") material.excluded = true;
        entry.material = material;
        completeness.set(featureId, entry);

        if (row.purchaseUnit && row.requiredUnits > 0 && !quantities.has(featureId)) {
          quantities.set(
            featureId,
            quantityComparison({
              unit: row.purchaseUnit,
              requiredX100: row.requiredUnits * 100,
              purchasedX100: row.fulfillment.purchasedUnitsX100,
            }),
          );
        }
      }
    }

    // Features the user has marked finished confirm their own bases.
    const statuses = await this.db.select<{ id: string; work_status: string | null }>(
      "SELECT id, work_status FROM project_features WHERE project_id = ?",
      [projectId],
    );
    for (const row of statuses) {
      if (row.work_status !== "complete") continue;
      const entry = completeness.get(row.id) ?? {};
      for (const basis of ["material", "labor", "other"] as CostBasis[]) {
        entry[basis] = { ...(entry[basis] ?? {}), userConfirmedComplete: true };
      }
      completeness.set(row.id, entry);
    }

    return reconcile({
      proposedLines,
      events,
      features: featureMeta,
      settlementByLineId: lineIndex,
      settlements: settlementRows.map((s) => ({
        id: s.id,
        description: s.description,
        eventId: s.expenseId ?? s.allocationId,
        coverageKind:s.coverageKind,
      })),
      completeness,
      quantities,
      // Manual expenses and mileage state their own basis; allocations
      // inherit it from the purchase line (see actualEvents).
      eventBasis: (event) => event.costType,
    });
  }

  /** Mark how far the physical work has got (cost-centric tracking only). */
  async setWorkStatus(featureId: string, status: "planned" | "in-progress" | "complete" | null): Promise<void> {
    await this.db.execute("UPDATE project_features SET work_status = ?, updated_at = ? WHERE id = ?", [
      status,
      new Date().toISOString(),
      featureId,
    ]);
  }

  async workStatuses(projectId: string): Promise<Map<string, string>> {
    const rows = await this.db.select<{ id: string; work_status: string | null }>(
      "SELECT id, work_status FROM project_features WHERE project_id = ? AND work_status IS NOT NULL",
      [projectId],
    );
    return new Map(rows.map((r) => [r.id, r.work_status!]));
  }

  /**
   * Where an actual number came from. Every figure resolves to the real
   * purchase / expense / mileage record behind it, including its receipt
   * attachments — the surface Phase 9D will enrich with extracted lines.
   */
  async provenance(eventIds: string[], projectId: string): Promise<ProvenanceRecord[]> {
    if (eventIds.length === 0) return [];
    const wanted = new Set(eventIds);
    const out: ProvenanceRecord[] = [];

    // ---- purchase allocations
    const graphs = await this.purchases.listTouchingProject(projectId);
    for (const graph of graphs) {
      const calc = computePurchase(graph);
      for (const alloc of graph.allocations) {
        if (!wanted.has(alloc.id)) continue;
        const c = calc.allocations.find((x) => x.allocationId === alloc.id);
        const line = graph.lines.find((l) => l.id === alloc.lineId);
        if (!c || !line) continue;
        const isReturn = graph.purchase.kind === "return";
        const lines: ProvenanceLine[] = [
          { label: line.description, amountCents: c.amountCents, detail: line.sku ? `SKU ${line.sku}` : null },
        ];
        if (c.shippingCents !== 0) lines.push({ label: "Allocated shipping", amountCents: c.shippingCents, detail: null });
        if (c.taxCents !== 0) lines.push({ label: "Allocated tax", amountCents: c.taxCents, detail: null });
        out.push({
          eventId: alloc.id,
          source: "allocation",
          sourceLabel: isReturn ? "Return / refund" : "Purchase",
          vendor: graph.purchase.vendor,
          date: graph.purchase.date,
          description: line.description,
          receiptNumber: graph.purchase.receiptNumber || null,
          amountCents: c.amountCents + c.shippingCents,
          taxCents: c.taxCents,
          totalCents: c.amountCents + c.shippingCents + c.taxCents,
          isReturn,
          lines,
          attachments: await this.attachments.listFor({ type: "purchase", id: graph.purchase.id }),
          purchaseId: graph.purchase.id,
          purchaseLineId: line.id,
          expenseId: null,
        });
      }
    }

    // ---- manual expenses
    const expenses = await this.expenses.listByProject(projectId);
    for (const expense of expenses) {
      if (!wanted.has(expense.id)) continue;
      out.push({
        eventId: expense.id,
        source: "expense",
        sourceLabel: "Manual entry",
        vendor: expense.vendor,
        date: expense.date,
        description: expense.description,
        receiptNumber: null,
        amountCents: expense.amountCents,
        taxCents: expense.taxCents,
        totalCents: expense.amountCents + expense.taxCents,
        isReturn: expense.amountCents < 0,
        lines: [
          { label: expense.description, amountCents: expense.amountCents, detail: expense.notes || null },
          ...(expense.taxCents !== 0 ? [{ label: "Sales tax", amountCents: expense.taxCents, detail: null }] : []),
        ],
        attachments: await this.attachments.listFor({ type: "expense", id: expense.id }),
        purchaseId: null,
        purchaseLineId: null,
        expenseId: expense.id,
      });
    }

    // ---- mileage
    const mileage = await this.mileage.listByProject(projectId);
    for (const entry of mileage) {
      if (!wanted.has(entry.id)) continue;
      out.push({
        eventId: entry.id,
        source: "mileage",
        sourceLabel: "Mileage",
        vendor: "",
        date: entry.date,
        description: entry.purpose,
        receiptNumber: null,
        amountCents: entry.amountCents,
        taxCents: 0,
        totalCents: entry.amountCents,
        isReturn: false,
        lines: [
          {
            label: `${entry.milesX10 / 10} miles × ${(entry.rateCentsPerMile / 100).toFixed(2)}/mile`,
            amountCents: entry.amountCents,
            detail: "Rate frozen when the trip was recorded",
          },
        ],
        attachments: [],
        purchaseId: null,
        purchaseLineId: null,
        expenseId: null,
      });
    }

    out.sort((a, b) => a.date.localeCompare(b.date));
    return out;
  }
}
